// Audience section — sits between demo and pricing. Section H2 + supporting
// body copy, then four audience cards in a responsive grid (1 col mobile,
// 2 cols sm, 4 cols lg). Each card is a self-contained product moment:
// Meta Blue icon glyph in a tinted badge, audience title, one-liner.
//
// Same card language as the pricing shell — hairline border, soft elevation
// on hover, no internal tone shifts. Card surface lifts to #1F2123 against
// the section's bg-black so each card reads as elevated.
//
// Per DESIGN.md: Display 2 (48px / 500) headline, Body (18 / 400 / 1.44)
// supporting paragraph, Heading 3 (18 / 700) card titles, Caption text on
// secondary descriptions, Meta Blue reserved for actionable / accent.
import type { ReactNode } from 'react';

import { CHANNEL_COPY, type Channel } from '../lib/channel-copy';

function ZapIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M4 14a1 1 0 0 1-.78-1.63l9.9-10.2a.5.5 0 0 1 .86.46l-1.92 6.02A1 1 0 0 0 13 10h7a1 1 0 0 1 .78 1.63l-9.9 10.2a.5.5 0 0 1-.86-.46l1.92-6.02A1 1 0 0 0 11 14z" />
    </svg>
  );
}

function CodeIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <polyline points="16 18 22 12 16 6" />
      <polyline points="8 6 2 12 8 18" />
    </svg>
  );
}

function RocketIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M4.5 16.5c-1.5 1.26-2 5-2 5s3.74-.5 5-2c.71-.84.7-2.13-.09-2.91a2.18 2.18 0 0 0-2.91-.09z" />
      <path d="M12 15l-3-3a22 22 0 0 1 2-3.95A12.88 12.88 0 0 1 22 2c0 2.72-.78 7.5-6 11a22.35 22.35 0 0 1-4 2z" />
      <path d="M9 12H4s.55-3.03 2-4c1.62-1.08 5 0 5 0" />
      <path d="M12 15v5s3.03-.55 4-2c1.08-1.62 0-5 0-5" />
    </svg>
  );
}

function UsersIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
  );
}

// AIT-268 — every audience names a channel now, so all four descriptions come
// from CHANNEL_COPY at render time and adapt on /whatsapp and /instagram.
type CopyKey = 'vibeCoders' | 'developers' | 'influencers' | 'agencies';

const AUDIENCES: { label: string; copyKey: CopyKey; Icon: () => ReactNode }[] = [
  { label: 'Vibe Coders', copyKey: 'vibeCoders', Icon: ZapIcon },
  { label: 'Developers', copyKey: 'developers', Icon: CodeIcon },
  { label: 'Influencers', copyKey: 'influencers', Icon: RocketIcon },
  { label: 'Agencies', copyKey: 'agencies', Icon: UsersIcon },
];

export function BuiltFor({ channel = 'both' }: { channel?: Channel }) {
  const copy = CHANNEL_COPY[channel];
  return (
    <section
      id="built-for"
      data-testid="built-for"
      className="bg-black px-6 py-16 md:py-24"
    >
      <div className="mx-auto max-w-5xl">
        <div className="mb-14 text-center">
          <h2
            className="text-4xl md:text-5xl font-medium tracking-tight leading-[1.17] text-white"
            style={{ letterSpacing: '-0.015em' }}
          >
            {copy.builtForHeadline}
          </h2>
          <p className="mx-auto mt-5 max-w-2xl text-lg leading-[1.44] text-white/60">
            {copy.builtForIntro}
          </p>
        </div>

        <ul className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {AUDIENCES.map(({ label, copyKey, Icon }) => (
            <li key={label}>
              <div className="flex h-full flex-col items-center gap-4 rounded-[20px] border border-white/[0.08] bg-[#181A1B] p-6 text-center">
                <span className="flex size-11 shrink-0 items-center justify-center rounded-full border border-[#0064E0]/40 bg-[#0064E0]/15 text-[#3B86FF]">
                  <Icon />
                </span>
                <div className="flex flex-col gap-1.5">
                  <h3 className="text-lg font-semibold leading-tight text-white">
                    {label}
                  </h3>
                  <p className="text-sm leading-[1.5] text-white/60">
                    {copy[copyKey]}
                  </p>
                </div>
              </div>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}
